import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Radio, Loader, Users, ChevronLeft } from "lucide-react";
import GroupListItem from "./GroupListItem";

const GroupListPanel = ({ loading, groups, broadcasts, search, onSearch, selectedGroup, onSelectGroup, onNewBroadcast, mobileView }) => {
  const navigate = useNavigate();
  const broadcastsByGroup = useMemo(() =>
    broadcasts.reduce((acc, b) => {
      if (!acc[b.groupId]) acc[b.groupId] = [];
      acc[b.groupId].push(b);
      return acc;
    }, {}),
  [broadcasts]);

  const sortedGroups = useMemo(() =>
    [...groups].sort((a, b) => {
      const la = broadcastsByGroup[a.id]?.[0]?.createdAt;
      const lb = broadcastsByGroup[b.id]?.[0]?.createdAt;
      if (!la && !lb) return 0;
      if (!la) return 1;
      if (!lb) return -1;
      return new Date(lb) - new Date(la);
    }),
  [groups, broadcastsByGroup]);

  const filtered = sortedGroups.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      className={`flex flex-col bg-white border-r border-gray-200 w-full md:w-80 lg:w-96 flex-shrink-0
        ${mobileView === "chat" ? "hidden md:flex" : "flex"}`}
      style={{ height: "100%" }}
    >
      {/* Header */}
      <div className="bg-[#075e54] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
            title="Go back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Radio className="w-5 h-5" />
          <h1 className="text-base font-semibold">Broadcasts</h1>
        </div>
        <button onClick={onNewBroadcast} className="p-2 rounded-full hover:bg-white/20 transition-colors" title="New Broadcast">
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search groups…"
            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Loader className="w-6 h-6 animate-spin text-gray-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-center px-6">
            <Users className="w-10 h-10 text-gray-200 mb-2" />
            <p className="text-sm text-gray-400">
              {groups.length === 0 ? "No groups yet." : "No groups match your search."}
            </p>
          </div>
        ) : (
          filtered.map((g) => (
            <GroupListItem
              key={g.id}
              group={g}
              lastBroadcast={broadcastsByGroup[g.id]?.[0] ?? null}
              isSelected={selectedGroup?.id === g.id}
              onClick={() => onSelectGroup(g, broadcastsByGroup)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default GroupListPanel;
